import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { api, ApiError } from "../api";
import "./estimate.css";

/**
 * The respondent page (ALN-48, docs/pages/estimate.md B "Respondent page"), as the mockup's
 * #survey-<id> view: one centered card, no app shell, mobile first. Opened three ways:
 *   /survey#t=<token>   a personal link (no session; the token stays in the fragment, so it isn't logged)
 *   /survey?mine=<id>   an invited person answering in the app (Your surveys, session routes)
 *   /survey?preview=<id> the organizer's preview of a draft (nothing is saved)
 * Answers stay on the device until they're submitted, so a lost signal loses nothing.
 */

type Size = "S" | "M" | "L";
const SIZES: Size[] = ["S", "M", "L"];
const SIZE_NAME: Record<Size, string> = { S: "Small", M: "Medium", L: "Large" };

interface RoundDto { id: string; title: string; question: string | null; countsNote: string | null; hoursPer: "job" | "month"; hourCards: number[]; bands: Record<Size, { range: string }> | null }
interface ViewDto {
  survey: { name: string; intro: string; winery: string; team: string; minutes: number; status: "open" | "closed" | "preview"; closesOn: string; closedOn: string | null; sender: string; person: string | null; rounds: RoundDto[] };
  answers: Record<string, Record<Size, number | null>>;
  notes: Record<string, string>;
}

/** One size's choice: a card's hours, Other with typed text, or Not sure. */
interface Pick { kind: "card" | "other" | "notsure" | null; hours: number | null; text: string }
type Draft = Record<string, { S: Pick; M: Pick; L: Pick; note: string }>;

const EMPTY: Pick = { kind: null, hours: null, text: "" };
const longDate = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", day: "numeric", timeZone: "UTC" });
const fmtH = (h: number) => (Number.isInteger(h) ? String(h) : String(h));
const ALL_ERR_ID = "resp-all-err";

/**
 * One size's hour cards as a radio group (TC-EST-021): one tab stop, arrow keys move and choose,
 * Home and End jump to the first card and Not sure.
 */
function HourCards({ cards, pick, labelledBy, describedBy, onPick }: { cards: number[]; pick: Pick; labelledBy: string; describedBy?: string; onPick: (p: Pick) => void }) {
  const opts: { label: string; pick: Pick; on: boolean }[] = [
    ...cards.map(h => ({ label: `${fmtH(h)} h`, pick: { kind: "card", hours: h, text: "" } as Pick, on: pick.kind === "card" && pick.hours === h })),
    { label: "Other", pick: { kind: "other", hours: null, text: pick.kind === "other" ? pick.text : "" }, on: pick.kind === "other" },
    { label: "Not sure", pick: { kind: "notsure", hours: null, text: "" }, on: pick.kind === "notsure" },
  ];
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = Math.max(0, opts.findIndex(o => o.on));
  const move = (to: number) => {
    const i = (to + opts.length) % opts.length;
    onPick(opts[i]!.pick);
    refs.current[i]?.focus();
  };
  const onKey = (e: KeyboardEvent, i: number) => {
    const to = { ArrowRight: i + 1, ArrowDown: i + 1, ArrowLeft: i - 1, ArrowUp: i - 1, Home: 0, End: opts.length - 1 }[e.key];
    if (to === undefined) return;
    e.preventDefault();
    move(to);
  };
  return (
    <div className="resp__cards" role="radiogroup" aria-labelledby={labelledBy} aria-describedby={describedBy}>
      {opts.map((o, i) => (
        <button key={o.label} ref={el => { refs.current[i] = el; }} type="button" role="radio" className="hcard" aria-checked={o.on} tabIndex={i === current ? 0 : -1}
          onClick={() => onPick(o.pick)} onKeyDown={e => onKey(e, i)}>{o.label}</button>
      ))}
    </div>
  );
}

type Source = { kind: "token"; token: string } | { kind: "mine"; id: string } | { kind: "preview"; id: string };

function sourceOf(): Source | null {
  const hash = new URLSearchParams(window.location.hash.slice(1));
  const t = hash.get("t");
  if (t) return { kind: "token", token: t };
  const q = new URLSearchParams(window.location.search);
  if (q.get("mine")) return { kind: "mine", id: q.get("mine")! };
  if (q.get("preview")) return { kind: "preview", id: q.get("preview")! };
  return null;
}

const paths = (s: Source) => s.kind === "token"
  ? { get: `/respond/${encodeURIComponent(s.token)}`, put: `/respond/${encodeURIComponent(s.token)}/answers`, store: `vintner.survey.t.${s.token.slice(0, 12)}` }
  : s.kind === "mine" ? { get: `/surveys/mine/${s.id}/answers`, put: `/surveys/mine/${s.id}/answers`, store: `vintner.survey.m.${s.id}` }
    : { get: `/surveys/${s.id}/preview`, put: null, store: null };

function readDraft(key: string | null): Draft | null {
  if (!key) return null;
  try { return JSON.parse(localStorage.getItem(key) ?? "null") as Draft | null; } catch { return null; }
}
function writeDraft(key: string | null, d: Draft | null) {
  if (!key) return;
  try { if (d) localStorage.setItem(key, JSON.stringify(d)); else localStorage.removeItem(key); } catch { /* private mode: answers stay in memory */ }
}

/** The stored answers as picks: a card when the hours are on a card, else Other. */
function draftFrom(v: ViewDto): Draft {
  const out: Draft = {};
  for (const r of v.survey.rounds) {
    const a = v.answers[r.id];
    const pick = (h: number | null | undefined): Pick => (a === undefined ? EMPTY : h == null ? { kind: "notsure", hours: null, text: "" } : r.hourCards.includes(h) ? { kind: "card", hours: h, text: "" } : { kind: "other", hours: h, text: String(h) });
    out[r.id] = { S: pick(a?.S), M: pick(a?.M), L: pick(a?.L), note: v.notes[r.id] ?? "" };
  }
  return out;
}

/** The exact messages of C (Pick hours for a size). */
function otherError(text: string): string | null {
  const t = text.trim();
  if (!t) return "Type the hours, or pick a card.";
  if (!/^\d*\.?\d+$/.test(t) || !Number.isFinite(Number(t))) return "Hours should be a number, like 1.5.";
  const n = Number(t);
  if (n < 0.25 || n > 999) return "Enter hours from 0.25 to 999.";
  if ((t.split(".")[1] ?? "").length > 2) return "Use at most two decimals, like 2.25.";
  return null;
}

type Screen = { kind: "loading" } | { kind: "invalid" } | { kind: "expired"; sender: string | null } | { kind: "error" } | { kind: "ready" };

export function RespondPage() {
  const source = useMemo(sourceOf, []);
  const p = source ? paths(source) : null;
  const [screen, setScreen] = useState<Screen>({ kind: source ? "loading" : "invalid" });
  const [view, setView] = useState<ViewDto | null>(null);
  const [draft, setDraft] = useState<Draft>({});
  const [step, setStep] = useState<number>(-1); // -1 welcome, 0..n-1 activities, n review, n+1 submitted
  const [errors, setErrors] = useState<Partial<Record<Size | "note" | "all", string>>>({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [closedNow, setClosedNow] = useState<string | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  useEffect(() => {
    if (!p) return;
    api<ViewDto>(p.get).then(v => {
      setView(v);
      const stored = draftFrom(v);
      const local = readDraft(p.store);
      setDraft(local ? { ...stored, ...Object.fromEntries(Object.entries(local).filter(([k]) => k in stored)) } : stored);
      // reopening a link that already answered shows Submitted, with Change my answers
      if (Object.keys(v.answers).length && !local) setStep(v.survey.rounds.length + 1);
      setScreen({ kind: "ready" });
    }).catch(e => {
      if (e instanceof ApiError && e.code === "link_expired") setScreen({ kind: "expired", sender: typeof e.details.sender === "string" ? e.details.sender : null });
      else if (e instanceof ApiError && (e.status === 404 || e.code === "link_invalid")) setScreen({ kind: "invalid" });
      else setScreen({ kind: "error" });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { titleRef.current?.focus({ preventScroll: true }); window.scrollTo(0, 0); }, [step, screen.kind]);

  const s = view?.survey;
  const rounds = s?.rounds ?? [];
  const n = rounds.length;
  const update = (rid: string, patch: Partial<Draft[string]>) => setDraft(d => {
    const next = { ...d, [rid]: { ...(d[rid] ?? { S: EMPTY, M: EMPTY, L: EMPTY, note: "" }), ...patch } };
    writeDraft(p?.store ?? null, next);
    return next;
  });

  const checkStep = (i: number): boolean => {
    const r = rounds[i];
    if (!r) return true;
    const d = draft[r.id];
    const errs: typeof errors = {};
    for (const z of SIZES) {
      const pk = d?.[z] ?? EMPTY;
      if (!pk.kind) errs[z] = "blank";
      else if (pk.kind === "other") { const m = otherError(pk.text); if (m) errs[z] = m; }
    }
    if ((d?.note ?? "").length > 500) errs.note = "Keep the note under 500 characters.";
    if (SIZES.some(z => errs[z] === "blank")) errs.all = "Give hours for each size, or choose Not sure.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  async function submit() {
    if (!p || !view) return;
    if (!p.put) { setStep(n + 1); return; } // preview: nothing is saved
    setSending(true);
    setSendError(null);
    const body = { answers: Object.fromEntries(rounds.map(r => {
      const d = draft[r.id]!;
      const h = (pk: Pick) => (pk.kind === "notsure" ? null : pk.kind === "other" ? Number(pk.text) : pk.hours);
      return [r.id, { S: h(d.S), M: h(d.M), L: h(d.L), note: d.note.trim() || null }];
    })) };
    try {
      await api(p.put, { method: "PUT", body });
      writeDraft(p.store, null);
      setStep(n + 1);
    } catch (e) {
      if (e instanceof ApiError && e.code === "survey_closed") setClosedNow(e.message);
      else if (e instanceof ApiError && e.code === "link_expired") setScreen({ kind: "expired", sender: typeof e.details.sender === "string" ? e.details.sender : null });
      else if (!navigator.onLine || !(e instanceof ApiError)) setSendError(navigator.onLine ? "Your answers didn't send. They're kept on this phone; try again." : "You're offline. Your answers are kept on this phone; submit again when you have signal.");
      else setSendError(e.message);
    } finally {
      setSending(false);
    }
  }

  const frame = (body: ReactNode, opts: { winery?: string; foot?: boolean } = {}) => (
    <div className="resp">
      <main className="resp__main">
        <p className="resp__brand">{opts.winery ? `${opts.winery} · Team estimate` : "Team estimate"}</p>
        {source?.kind === "preview" && <p className="resp__banner" role="note">Preview. This survey hasn't been sent yet, so nothing you enter here is saved.</p>}
        {offline && screen.kind === "ready" && <p className="resp__banner resp__banner--warn" role="status">You're offline. Your answers are kept on this phone; submit again when you have signal.</p>}
        <section className="resp__card">{body}</section>
        {opts.foot && s && <p className="resp__foot">Your answers are anonymous. Nobody, including {s.sender}, sees who gave which answer.</p>}
      </main>
    </div>
  );

  if (screen.kind === "loading") return frame(<div aria-busy="true"><div className="skel skel--title" /><div className="skel" /><div className="skel" /></div>);
  if (screen.kind === "invalid") return frame(<><h1 className="resp__t" tabIndex={-1} ref={titleRef}>This survey link doesn't work</h1><p>The survey may have been removed, or the link was cut off when it was copied. Ask the person who sent it for a fresh link.</p></>);
  if (screen.kind === "expired") return frame(<><h1 className="resp__t" tabIndex={-1} ref={titleRef}>This link has expired</h1><p>Ask {screen.sender ?? "the person who sent it"} for a new link. Any answers you already submitted are kept.</p></>);
  if (screen.kind === "error" || !s) return frame(<><h1 className="resp__t" tabIndex={-1} ref={titleRef}>The survey couldn't load</h1><p>Check your signal and try again. Nothing was changed.</p><button type="button" className="btn" onClick={() => window.location.reload()}>Try again</button></>);

  if (s.status === "closed") {
    return frame(<><h1 className="resp__t" tabIndex={-1} ref={titleRef}>This survey has closed</h1><p>It closed on {longDate(s.closedOn ?? s.closesOn)}, and {s.sender} is looking at the results now. Thank you.</p></>, { winery: s.winery });
  }
  if (closedNow) return frame(<><h1 className="resp__t" tabIndex={-1} ref={titleRef}>This survey has closed</h1><p role="alert">{closedNow}</p></>, { winery: s.winery });

  // Welcome
  if (step === -1) {
    return frame(<>
      <p className="resp__kicker">{s.team} · {n} {n === 1 ? "activity" : "activities"} · about {s.minutes} minutes</p>
      <h1 className="resp__t resp__t--serif" tabIndex={-1} ref={titleRef}>{s.name}</h1>
      <p className="resp__from">From {s.sender}, {s.winery}</p>
      <h2 className="resp__h">What you're doing</h2>
      <p>Estimating how many hours some of the team's work takes, for a small, a medium and a large job.</p>
      {s.intro && <><h2 className="resp__h">Why we're asking</h2><p>{s.intro}</p></>}
      <h2 className="resp__h">How to answer</h2>
      <ol className="resp__how">
        <li>For each activity, think of a small, a medium and a large job.</li>
        <li>Give your best guess in hours for each. Choose Not sure if you don't know.</li>
        <li>You can change your answers until the survey closes.</li>
      </ol>
      <div className="resp__nav resp__nav--end"><button type="button" className="btn btn--primary btn--lg" onClick={() => setStep(0)} disabled={!n}>Start</button></div>
    </>, { winery: s.winery, foot: true });
  }

  // Submitted
  if (step === n + 1) {
    return frame(<>
      <h1 className="resp__t" tabIndex={-1} ref={titleRef}>Your answers are in</h1>
      <p>Thank you. Results stay hidden from everyone until the survey closes on {longDate(s.closesOn)}. Then the team's typical answer for each size becomes the starting number in the plans.</p>
      <div className="resp__nav"><button type="button" className="btn btn--lg" onClick={() => setStep(0)}>Change my answers</button></div>
    </>, { winery: s.winery, foot: true });
  }

  // Review
  if (step === n) {
    const show = (pk: Pick) => (pk.kind === "notsure" ? "Not sure" : pk.kind === "other" ? `${pk.text.trim()} h` : pk.hours != null ? `${fmtH(pk.hours)} h` : "Not answered");
    return frame(<>
      <p className="resp__kicker">Almost done</p>
      <h1 className="resp__t" tabIndex={-1} ref={titleRef}>Check your answers</h1>
      <ul className="resp__review">
        {rounds.map((r, i) => {
          const d = draft[r.id];
          return (
            <li key={r.id}>
              <div><b>{r.title}</b>
                <span className="resp__sizes">{SIZES.map(z => `${SIZE_NAME[z]} ${d ? show(d[z]) : "Not answered"}`).join(" · ")}</span>
                {d?.note && <span className="resp__note">“{d.note.length > 80 ? `${d.note.slice(0, 80)}…` : d.note}”</span>}
              </div>
              <button type="button" className="btn btn--lg" onClick={() => { setErrors({}); setStep(i); }} aria-label={`Change ${r.title}`}>Change</button>
            </li>
          );
        })}
      </ul>
      <p className="resp__muted">You can change your answers until the survey closes on {longDate(s.closesOn)}.</p>
      {sendError && <p className="resp__alert" role="alert">{sendError}</p>}
      <div className="resp__nav">
        <button type="button" className="btn btn--lg" onClick={() => setStep(n - 1)}>Back</button>
        <button type="button" className="btn btn--primary btn--lg" onClick={() => void submit()} disabled={sending}>{sending ? "Submitting…" : "Submit my answers"}</button>
      </div>
    </>, { winery: s.winery, foot: true });
  }

  // One activity
  const r = rounds[step]!;
  const d = draft[r.id] ?? { S: EMPTY, M: EMPTY, L: EMPTY, note: "" };
  const next = () => { if (checkStep(step)) { setErrors({}); setStep(step + 1); } };
  return frame(<>
    <progress className="resp__progress" aria-label={`Activity ${step + 1} of ${n}`} max={n} value={step + 1} />
    <p className="resp__kicker">Activity {step + 1} of {n}</p>
    <h1 className="resp__t resp__t--serif" tabIndex={-1} ref={titleRef}>{r.title}</h1>
    {r.question && <p className="resp__q">{r.question}</p>}
    {r.countsNote && <p className="resp__counts"><b>What counts:</b> {r.countsNote}</p>}
    <h2 className="resp__h">{r.hoursPer === "month" ? "Hours each month" : "Hours for the whole job"}</h2>
    {SIZES.map(z => {
      const pk = d[z];
      const err = errors[z];
      const gid = `sz-${r.id}-${z}`;
      return (
        <fieldset key={z} className={`resp__size${err ? " is-invalid" : ""}`} aria-invalid={err ? true : undefined} aria-describedby={err && err !== "blank" ? `${gid}-err` : undefined}>
          <legend id={`${gid}-l`}><b>{SIZE_NAME[z]}</b>{r.bands && <span className="resp__range"> ({r.bands[z].range})</span>}</legend>
          <HourCards cards={r.hourCards} pick={pk} labelledBy={`${gid}-l`} describedBy={err === "blank" && errors.all ? ALL_ERR_ID : undefined}
            onPick={p => update(r.id, { [z]: p })} />
          {pk.kind === "other" && (
            <label className="field resp__other" htmlFor={`${gid}-o`}>
              <span>Other, in hours</span>
              <span className="resp__otherrow"><input className="input" id={`${gid}-o`} inputMode="decimal" autoComplete="off" value={pk.text} aria-invalid={err && err !== "blank" ? true : undefined}
                onChange={e => update(r.id, { [z]: { kind: "other", hours: null, text: e.target.value } })} /> <span aria-hidden="true">h</span></span>
            </label>
          )}
          {err && err !== "blank" && <p className="field__error" id={`${gid}-err`}>{err}</p>}
        </fieldset>
      );
    })}
    <label className="field" htmlFor={`note-${r.id}`}>
      <span>Anything we should know? <span className="resp__muted">(optional)</span></span>
      <textarea className="input textarea" id={`note-${r.id}`} rows={3} maxLength={600} placeholder="For example: it depends a lot on the vineyard" value={d.note} aria-invalid={errors.note ? true : undefined}
        onChange={e => update(r.id, { note: e.target.value })} />
      <small>Shown to the team after the survey closes, without your name. Leave out anything that says who you are.</small>
      {errors.note && <span className="field__error">{errors.note}</span>}
    </label>
    {errors.all && <p className="resp__alert" role="alert" id={ALL_ERR_ID}>{errors.all}</p>}
    <div className="resp__nav">
      <button type="button" className="btn btn--lg" onClick={() => { setErrors({}); setStep(step - 1); }}>← Back</button>
      <button type="button" className="btn btn--primary btn--lg" onClick={next}>{step === n - 1 ? "Review answers" : "Next →"}</button>
    </div>
  </>, { winery: s.winery, foot: true });
}
